import { groupBy, sort } from 'rambda';
import { type Reducer, useEffect, useReducer } from 'react';
import useCollections, { type Collection } from '@/hooks/collections/use-collections';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { getDateType } from '@/utils/parseTime/parse-date';
import { generateSessionKey } from '@/utils/session';

export default function useFilteredCollections(filterType: CollectionFilterReducerFilterType = 'all') {
  const { collections, isLoading, error } = useCollections();
  const [{ filteredBy, collections: filteredCollections }, dispatch] = useReducer<CollectionFilterReducer>(
    collectionFilterReducer,
    createInitialState(filterType, collections),
  );

  useEffect(() => {
    if (collections) {
      dispatch({
        type: 'update',
        params: { collections },
      });
    }
  }, [collections]);

  return {
    filteredCollections,
    filterType: filteredBy,
    setFilterType: (type: CollectionFilterReducerFilterType) => dispatch({ type, params: { collections } }),
    isLoading,
    error,
  };
}

export type CollectionFilterReducerFilterType = 'all' | 'media' | 'text' | 'files';

type CollectionFilterReducerAction =
  | {
      type: CollectionFilterReducerFilterType;
      params: {
        collections: Collection[];
      };
    }
  | { type: 'update'; params: { collections: Collection[] } };

type CollectionFilterReducerState = {
  filteredBy: CollectionFilterReducerFilterType;
  collections: Partial<Record<string, Collection[]>>;
};

type CollectionFilterReducer = Reducer<CollectionFilterReducerState, CollectionFilterReducerAction>;

function createInitialState(
  filterType: CollectionFilterReducerFilterType,
  collections: Collection[],
): CollectionFilterReducerState {
  return collectionFilterReducer(
    {
      filteredBy: filterType,
      collections: {},
    },
    {
      type: filterType,
      params: { collections },
    },
  );
}

function collectionFilterReducer(
  state: CollectionFilterReducerState,
  action: CollectionFilterReducerAction,
): CollectionFilterReducerState {
  const collections = action.params.collections;

  switch (action.type) {
    case 'update':
      return {
        ...state,
        collections: groupCollections(filterByType(state.filteredBy, action.params.collections)),
      };
    default:
      return {
        ...state,
        filteredBy: action.type,
        collections: groupCollections(filterByType(action.type, collections)),
      };
  }
}

function filterByType(filter: CollectionFilterReducerFilterType, collections: Collection[]) {
  switch (filter) {
    case 'media':
      return collections.filter((c) =>
        [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(c.Type),
      );
    case 'text':
      return collections.filter((c) => c.Type === ConversationMessageContentType.Text);
    case 'files':
      return collections.filter((c) => c.Type === ConversationMessageContentType.File);
    default:
      return collections;
  }
}

function groupCollections(collections: Collection[]) {
  return groupBy(
    (item) => `${generateSessionKey(item.FromType, item.FromID)}-${getDateType(item.TimeStamp) ?? ''}` as string,
    sort<Collection>((c) => -c.TimeStamp, collections),
  );
}
